import { Component, Input, OnInit, Inject, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { firstValueFrom } from 'rxjs';

import { SHARED_IMPORTS } from '../../shared/shared-imports';
import { ContentService } from '../../../services/content.service';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';


@Component({
    selector: 'new-unit-template',
    standalone: true,
    imports: [
        ...SHARED_IMPORTS,
        MatIconModule,
        FormsModule,
        MatButtonModule,
        ConfirmDialogComponent,
        MatSnackBarModule
    ],
    templateUrl: 'components/manage-unit-templates/new-unit-template/new-unit-template.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class NewUnitTemplateComponent implements OnInit {

    private content = inject(ContentService);
    private http = inject(HttpClient);
    private dialog = inject(MatDialog);
    private snack = inject(MatSnackBar);

    pressureValues: any[] = [
        { id: true, label: '+' },
        { id: false, label: '-' }
    ];

    constructor(


    ) { }


    conditionNames = [
        { tempPropTarget: "TemperatureTarget", tempPropAllowedDev: "TemperatureAllowedDeviation", name: "Temperature", unit: "°C", unitType: "IVC IsoCage SC+Scantainer" },
        { tempPropTarget: "CageHumidityTarget", tempPropAllowedDev: "CageHumidityAllowedDeviation", name: "Humidity", unit: "%", unitType: "IVC IsoCage SC+Scantainer" },
        { tempPropTarget: "AirChangeTarget", tempPropAllowedDev: "AirChangeAllowedDeviation", name: "AirChange", unit: "x", unitType: "IVC SC+Scantainer" },
        { tempPropTarget: "AirFlowDiffTarget", tempPropAllowedDev: "AirFlowDiffAllowedDeviation", name: "AirFlowDiff", unit: "%", unitType: "IVC" },
        { tempPropTarget: "IsPressurePositive", name: "PressureDiff", unit: "", unitType: "IVC" },
        { tempPropTarget: "ExhaustAirFlowAllowedMinimum", name: "ExhaustAirFlow", unit: "m3/h", unitType: "IsoCage" },
        { tempPropTarget: "UpsPctBatteryAllowedMinimum", name: "UpsBattery", unit: "%", unitType: "IsoCage" },
        { tempPropTarget: "PressurePaTarget", tempPropAllowedDev: "PressurePaAllowedDeviation", name: "Pressure", unit: "Pa", unitType: "IsoCage SC+Scantainer" },
        { name: "UsedOn", unit: "positions", unitType: "IsoCage IVC SC+Scantainer" }
    ];
    // Data
    @Input() template!: any;
    @Input() unitType: string;

    // Flags
    @Input() readMode = false;
    @Input() templateNotSaved = false;

    // Callbacks from parent — provide safe defaults to avoid runtime errors
    @Input() removeRedBorder: () => void = () => { };
    @Input() sortTemplates: () => void = () => { };
    @Input() currentTemplateName: string = '';
    @Input() templates: any[] = [];
    @Input() templateSavedFeedback: (t: any) => void = () => { };
    @Input() copiedTemplate: any = null;
    @Input() exitEditMode: (t: any) => void = () => { };
    @Input() getTemplates: () => void = () => { };
    @Input() ensureTemplateShape: (t: any) => any = () => { };
    @Input() addOrUpdateTemplate: (t: any, selectedUnitType: string | null) => void = () => { };

    canEdit(template): boolean {
        return !this.readMode && !this.checkIfUsed(template);
    }

    checkIfUsed(template: any): boolean {
        return template != null && template.UsedOn > 0;
    }

    showDelta(header: string): boolean {
        return header !== 'PressureDiff' && header !== 'UsedOn' && header !== 'ExhaustAirFlow' && header !== 'UpsBattery';
    }

    showColumn(headerObj: any): boolean {
        return headerObj.unitType.includes(this.unitType);
    }

    isHeader(currentHeader: string, header: string): boolean {
        return currentHeader === header;
    }

    getTemplateWidthClass(unitType: string): string {
        if (unitType === 'SC+Scantainer') {
            return 'scantainerTemplateWidth';
        } else if (unitType === 'IVC') {
            return 'ivcIsoCageTemplateWidth';
        } else {
            return 'isoCageTemplateWidth';
        }
    }
    getHeaderTitle(header: string, unitType: string): string {
        if (header.includes('Pressure') && unitType === 'IVC') {
            header = 'PressureIvc';
        }
        return this.content.getContent(`ManageUnitTemplates.${header}`);
    }

    validationRequired(template: any): boolean {
        if (template !== undefined && template !== null) {
            return template.checkIfNull || template.Name === null || template.Name === "";
        } else {
            return false;
        }
    }

    checkAllValues(template: any): void {
        const keyNames = Object.keys(template.UnitConditionTemp);
        let counter = 0;

        for (const key of keyNames) {
            if (template.UnitConditionTemp[key] != null && template.UnitConditionTemp[key] !== "") {
                counter++;
            }
        }

        template.checkIfNull = counter > 0 ? false : true;
    }

    updateTemplateName(template: any): void {
        if (template.Name !== this.currentTemplateName) {
            const url =
                `/api/UnitConditionTemplate/UpdateUnitConditionTemplateName` +
                `?templateId=${template.Id}&templateName=${encodeURIComponent(template.Name)}`;

            this.http.post<string>(url, template).subscribe({
                next: (message) => {
                    if (message && message !== '') {
                        template.Message = message;
                    } else {
                        template.Message = '';
                        template.titleEditMode = false;
                    }
                    this.sortTemplates();
                },
                error: (error) => {
                    template.Message = error.error.text;
                },
            });
        } else {
            template.titleEditMode = false;
        }
    }

    public copyTemplate(template: any): void {
        const copied = {
            Id: null,
            Name: `${template.Name} Copy`,
            Description: template.Description,
            FirstApplicationTimestamp: null,
            UsedOn: 0,
            UnitConditionTemp: { ...template.UnitConditionTemp },
            editMode: true,
            copy: true,
        };

        this.copiedTemplate = copied;
        this.templates.unshift(this.copiedTemplate);
        // Animate scroll to top with smooth scrolling
        this.smoothScrollToTop(300);
    }

    private smoothScrollToTop(duration: number): void {
        // Get the current scroll position more reliably
        const start = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        
        // If we're already at the top, no need to scroll
        if (start === 0) return;
        
        const startTime = performance.now();

        const animateScroll = (currentTime: number) => {
            const timeElapsed = currentTime - startTime;
            const progress = Math.min(timeElapsed / duration, 1);
            
            // Easing function for smooth animation
            const easeProgress = progress < 0.5 
                ? 2 * progress * progress 
                : 1 - Math.pow(-2 * progress + 2, 2) / 2;
            
            const currentPosition = start * (1 - easeProgress);
            
            // Use multiple methods to ensure scrolling works
            window.scrollTo(0, currentPosition);
            document.documentElement.scrollTop = currentPosition;
            document.body.scrollTop = currentPosition;
            
            if (progress < 1) {
                requestAnimationFrame(animateScroll);
            }
        };

        requestAnimationFrame(animateScroll);
    }

    deleteUnitTemplate(template: any): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.content.getContent('ManageUnitTemplates.DeleteTemplate'),
                okText: this.content.getContent('Standard.Delete'),
                cancelText: this.content.getContent('Standard.Cancel')
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) { // result === true if confirmed
                if (template.Id != null) {
                    this.deleteTemplate(template);
                } else {
                    this.exitEditMode(template);
                }
            }
        });
    }

    deleteTemplate(template: any): void {
        const params = new HttpParams().set('templateId', template.Id);
        this.http.post('/api/UnitConditionTemplate/DeleteUnitConditionTemplate', null, { params })
            .subscribe({
                next: () => {
                    const i = this.templates.indexOf(template);
                    if (i > -1) this.templates.splice(i, 1);
                    this.exitEditMode(template);
                   // this.getTemplates();
                },
                error: (err) => {
                    console.error('Failed to delete template', err);
                }
            });
    }
    cancelTemplateName(template: any): void {
        template.titleEditMode = false;
        template.Name = this.currentTemplateName;
    }

    enterEditMode(template: any): void {
        const elementPos = this.templates.indexOf(template);
        this.templates[elementPos].Message = "";
        this.templates[elementPos].OldName = this.templates[elementPos].Name;
        this.templates[elementPos].OldTemplate = {
            TemperatureTarget: null,
            TemperatureAllowedDeviation: null,
            CageHumidityTarget: null,
            CageHumidityAllowedDeviation: null,
            AirChangeTarget: null,
            AirChangeAllowedDeviation: null,
            AirFlowDiffTarget: null,
            AirFlowDiffAllowedDeviation: null,
            IsPressurePositive: null,
            ExhaustAirFlowAllowedMinimum: null,
            PressurePaAllowedDeviation: null,
            PressurePaTarget: null,
            UpsPctBatteryAllowedMinimum: null
        };

        this.copyValues(template);
        this.templateNotSaved = false;
        template.editMode = true;
    }

    copyValues(template: any): void {
        const keyNames = Object.keys(this.templates[0].UnitConditionTemp);
        for (const key of keyNames) {
            template.OldTemplate[key] = template.UnitConditionTemp[key];
        }
    }


    enterTitleEditMode(template: any): void {
        template.Message = "";
        template.titleEditMode = true;
        this.currentTemplateName = template.Name;
    }
    getUsedOnUnit(usedOn: number): string {
        return usedOn === 1
            ? this.content.getContent("ManageUnitTemplates.Position")
            : this.content.getContent("ManageUnitTemplates.Positions");
    }
    getTemplateNoValueClass(template: any, header: any, type: 'target' | 'allowedDev'): string {
        if (!template || !header) return '';

        if (type === 'target') {
            return header.tempPropTarget &&
                template.UnitConditionTemp?.[header.tempPropTarget] == null &&
                !template.editMode
                ? 'templateNoValue'
                : '';
        }

        if (type === 'allowedDev') {
            return header.tempPropAllowedDev &&
                template.UnitConditionTemp?.[header.tempPropAllowedDev] == null &&
                !template.editMode
                ? 'templateNoValue'
                : '';
        }

        return '';
    }

    updateTemplateValue(template: any, header: any, type: 'target' | 'allowedDev', value: any): void {
        if (!template?.UnitConditionTemp) return;

        if (type === 'target' && header?.tempPropTarget) {
            template.UnitConditionTemp[header.tempPropTarget] = value;
        } else if (type === 'allowedDev' && header?.tempPropAllowedDev) {
            template.UnitConditionTemp[header.tempPropAllowedDev] = value;
        }

        this.checkAllValues(template);
    }



    ngOnInit() {
    }
}
